function toFiniteNumber(value) {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
}

export function getHitScore(hit) {
  if (!hit) return 0;

  const rankFusionScore = toFiniteNumber(
    hit.hybrid_search_info?.rank_fusion_score ??
      hit.document?.hybrid_search_info?.rank_fusion_score
  );

  if (rankFusionScore !== null) {
    return rankFusionScore;
  }

  const vectorDistance = toFiniteNumber(
    hit.vector_distance ?? hit.document?.vector_distance
  );

  if (vectorDistance !== null) {
    return 1 - vectorDistance;
  }

  const textMatch = toFiniteNumber(hit.text_match ?? hit.document?.text_match);

  if (textMatch !== null) {
    return textMatch;
  }

  return 0;
}

function getScoreSource(groupedHits) {
  if (!Array.isArray(groupedHits) || groupedHits.length === 0) {
    return "none";
  }

  if (
    groupedHits.some(
      (groupedHit) =>
        toFiniteNumber(
          groupedHit.hybrid_search_info?.rank_fusion_score ??
            groupedHit.document?.hybrid_search_info?.rank_fusion_score
        ) !== null
    )
  ) {
    return "rank_fusion_score";
  }

  if (
    groupedHits.some(
      (groupedHit) =>
        toFiniteNumber(
          groupedHit.vector_distance ?? groupedHit.document?.vector_distance
        ) !== null
    )
  ) {
    return "vector_distance";
  }

  if (
    groupedHits.some(
      (groupedHit) =>
        toFiniteNumber(
          groupedHit.text_match ?? groupedHit.document?.text_match
        ) !== null
    )
  ) {
    return "text_match";
  }

  return "none";
}

export function scoreResultCard(hit) {
  if (!hit) {
    return {
      score: 0,
      chunkCount: 0,
      scoredChunkCount: 0,
      breakdown: [],
      maxScore: 0,
      minScore: 0,
      scoreSource: "none",
    };
  }

  const groupedHits =
    Array.isArray(hit._grouped_hits) && hit._grouped_hits.length > 0
      ? hit._grouped_hits
      : [hit];
  const chunkScores = groupedHits.map((groupedHit) => getHitScore(groupedHit));
  const totalScore = chunkScores.reduce((sum, score) => sum + score, 0);

  return {
    score: Math.round(totalScore * 10000) / 10000,
    chunkCount: hit._group_found || groupedHits.length,
    scoredChunkCount: groupedHits.length,
    breakdown: chunkScores,
    maxScore: chunkScores.length ? Math.max(...chunkScores) : 0,
    minScore: chunkScores.length ? Math.min(...chunkScores) : 0,
    scoreSource: getScoreSource(groupedHits),
  };
}

function groupHitsByPrimaryKey(hits) {
  const groups = new Map();

  hits.forEach((hit, originalIndex) => {
    const primaryKey = hit?.primary_key ?? hit?.document?.primary_key;
    if (primaryKey == null) return;

    const score = getHitScore(hit);
    const existing = groups.get(primaryKey);

    if (existing) {
      existing.totalScore += score;
      existing.chunks.push(hit);
      if (score > existing.bestScore) {
        existing.bestScore = score;
        existing.representative = hit;
      }
    } else {
      groups.set(primaryKey, {
        primaryKey,
        firstIndex: originalIndex,
        totalScore: score,
        bestScore: score,
        representative: hit,
        chunks: [hit],
      });
    }
  });

  return Array.from(groups.values());
}

export function sortResultsByRelevance(hits) {
  if (!Array.isArray(hits) || hits.length === 0) return [];

  return groupHitsByPrimaryKey(hits)
    .sort((firstGroup, secondGroup) => {
      const scoreDifference = secondGroup.totalScore - firstGroup.totalScore;

      return scoreDifference || firstGroup.firstIndex - secondGroup.firstIndex;
    })
    .map((group) => {
      const chunkScores = group.chunks.map((chunk) => getHitScore(chunk));

      return {
        ...group.representative,
        _grouped_hits: group.chunks,
        _relevanceScore: {
          score: Math.round(group.totalScore * 10000) / 10000,
          chunkCount: group.chunks.length,
          scoredChunkCount: group.chunks.length,
          breakdown: chunkScores,
          maxScore: chunkScores.length ? Math.max(...chunkScores) : 0,
          minScore: chunkScores.length ? Math.min(...chunkScores) : 0,
          scoreSource: getScoreSource(group.chunks),
        },
      };
    });
}

export function debugScoring(hit, searchType = "All", verbose = false) {
  const scoreData = scoreResultCard(hit);

  console.group("Relevance Scoring Debug");
  console.log("Result:", hit.standard_casenumber || hit.primary_key);
  console.log("Search Type:", searchType);
  console.log("Matching Chunks:", scoreData.chunkCount);
  console.log("Scored Chunks:", scoreData.scoredChunkCount);
  console.log("Total Score:", scoreData.score);
  console.log("Score Source:", scoreData.scoreSource);
  console.log(
    "Score Range:",
    `${scoreData.minScore.toFixed(4)} - ${scoreData.maxScore.toFixed(4)}`
  );

  if (verbose) {
    console.log("Individual Chunk Scores:", scoreData.breakdown);
  }

  console.groupEnd();
}
